import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import {
  IconBuildingStore,
  IconCategory2,
  IconTool,
  IconUsers,
  IconSettings,
  IconFileText,
  IconClipboardCheck,
  IconPackage,
  IconPrinter,
  IconDeviceTv,
  IconKey,
  IconFunction,
  IconGitBranch,
  IconFileInvoice,
  IconRepeat,
  IconScale,
  IconChartBar,
  IconCheckbox,
  IconListCheck,
  IconSelector,
  IconFileDescription,
  IconLayoutGrid,
  IconBell,
  IconDeviceDesktop,
  IconCloud,
  IconTruckLoading,
  IconServer,
  IconShieldLock,
  IconShieldCheck,
  IconBuildingBank,
  IconWorld,
  IconWorldCog,
  IconAutomation,
  IconReportMoney,
  IconMessage2,
  IconBrandWindows,
} from '@tabler/icons-react';
import './AdminPage.css';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { CARD_SX } from '../styles/designSystem';
import type { UpdateVendorRequest, Vendor } from '@techgitter/contracts';
import { getVendors, createVendor, updateVendor } from '../services/vendorService';
import { VendorEditDialog } from '../components/admin/VendorEditDialog';
import { VendorMappingsPage } from './VendorMappingsPage';
import { TaskListsAdminPage } from './TaskListsAdminPage';
import { TaskDropdownsAdminPage } from './TaskDropdownsAdminPage';
import { TemplateTaskConfigAdminPage } from './TemplateTaskConfigAdminPage';
import { TicketTypeTemplatesAdminPage } from './TicketTypeTemplatesAdminPage';
import { SchedulerAdminPage } from './SchedulerAdminPage';
import { ProcessesAdminPage } from './ProcessesAdminPage';
import { InvoiceScheduleAdminPage } from './InvoiceScheduleAdminPage';
import { AlertsPage } from './AlertsPage';
import { RepairShoprSyncPage } from './RepairShoprSyncPage';
import { DomainManagerPage } from './DomainManagerPage';
import { DomainsPage } from './DomainsPage';
import { XeroPage } from './XeroPage';
import { UsersAdminPage } from './UsersAdminPage';
import { RepairServicesAdminPage } from './RepairServicesAdminPage';
import { CustomerServicesCatalogAdminPage } from './CustomerServicesCatalogAdminPage';
import { AppSettingsAdminPage } from './AppSettingsAdminPage';
import { QuoteTermsTemplatesAdminPage } from './QuoteTermsTemplatesAdminPage';
import { CheckInTermsTemplatesAdminPage } from './CheckInTermsTemplatesAdminPage';
import { BundlesAdminPage } from './BundlesAdminPage';
import { LicenseInventoryAdminPage } from './LicenseInventoryAdminPage';
import { PrintersAdminPage } from './PrintersAdminPage';
import { AutoPrintRulesAdminPage } from './AutoPrintRulesAdminPage';
import { WallDisplayAdminPage } from './WallDisplayAdminPage';
import { ReconciliationPage } from './ReconciliationPage';
import { ReportingPage } from './ReportingPage';
import { SeatLedgerPage } from './SeatLedgerPage';
import { ProductPricingPage } from './ProductPricingPage';
import { ToDoTasksAdminPage } from './ToDoTasksAdminPage';
import { RecurringTasksAdminPage } from './RecurringTasksAdminPage';
import { EsetIntegrationAdminPage } from './EsetIntegrationAdminPage';
import { M365TenantMapAdminPage } from './M365TenantMapAdminPage';
import { SmsTestAdminPage } from './SmsTestAdminPage';

type AdminTab =
  | 'vendors' | 'services' | 'service-catalog' | 'inventory' | 'users'
  | 'app-settings'
  | 'quote-terms'
  | 'check-in-terms'
  | 'bundles'
  | 'license-inventory'
  | 'printers'
  | 'autoprint-rules'
  | 'wall-display'
  | 'functions' | 'invoices' | 'recurring-tasks' | 'processes'
  | 'task-lists' | 'task-dropdowns' | 'task-templates' | 'ticket-templates'
  | 'todos'
  | 'int-repairshopr' | 'int-pax8' | 'int-leader'
  | 'int-atera' | 'int-eset' | 'int-bitdefender' | 'int-m365'
  | 'int-xero' | 'int-domains' | 'int-domains-list'
  | 'reconciliation' | 'reporting' | 'product-pricing' | 'seat-ledger'
  | 'alerts'
  | 'sms-test';

type NavIcon = React.ComponentType<{ className?: string }>;

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  label: string;
  value: AdminTab;
  icon?: NavIcon;
  items?: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Config',
    items: [
      { label: 'Vendors', value: 'vendors', icon: IconBuildingStore },
      { label: 'Repair Services', value: 'services', icon: IconTool },
      { label: 'Service Catalog', value: 'service-catalog', icon: IconCategory2 },
      { label: 'Users', value: 'users', icon: IconUsers },
      { label: 'App Settings', value: 'app-settings', icon: IconSettings },
      { label: 'Quote Terms', value: 'quote-terms', icon: IconFileText },
      { label: 'Check-In Terms', value: 'check-in-terms', icon: IconClipboardCheck },
      { label: 'Bundles', value: 'bundles', icon: IconPackage },
      { label: 'License Inventory', value: 'license-inventory', icon: IconKey },
      { label: 'Printers', value: 'printers', icon: IconPrinter },
      { label: 'AutoPrint Rules', value: 'autoprint-rules', icon: IconAutomation },
      { label: 'Wall Display', value: 'wall-display', icon: IconDeviceTv },
    ],
  },
  {
    label: 'Schedules',
    items: [
      { label: 'Functions', value: 'functions', icon: IconFunction },
      { label: 'Processes', value: 'processes', icon: IconGitBranch },
      { label: 'Invoices', value: 'invoices', icon: IconFileInvoice },
      { label: 'Recurring Tasks', value: 'recurring-tasks', icon: IconRepeat },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { label: 'Reconciliation', value: 'reconciliation', icon: IconScale },
      { label: 'Reporting', value: 'reporting', icon: IconChartBar },
      { label: 'Seat Ledger', value: 'seat-ledger', icon: IconUsers },
      { label: 'Product Pricing', value: 'product-pricing', icon: IconReportMoney },
    ],
  },
  {
    label: 'Tasks',
    items: [
      { label: 'To-Do Tasks', value: 'todos', icon: IconCheckbox },
    ],
  },
  {
    label: 'Workflow',
    items: [
      { label: 'Task Lists', value: 'task-lists', icon: IconListCheck },
      { label: 'Task Dropdowns', value: 'task-dropdowns', icon: IconSelector },
      { label: 'Task Templates', value: 'task-templates', icon: IconFileDescription },
      { label: 'Ticket Templates', value: 'ticket-templates', icon: IconLayoutGrid },
    ],
  },
  {
    label: 'Comms',
    items: [
      { label: 'SMS Test', value: 'sms-test', icon: IconMessage2 },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { label: 'Alerts', value: 'alerts', icon: IconBell },
      {
        label: 'Suppliers',
        value: 'int-repairshopr',
        items: [
          { label: 'RepairShopr', value: 'int-repairshopr', icon: IconDeviceDesktop },
          { label: 'Pax8', value: 'int-pax8', icon: IconCloud },
          { label: 'Leader', value: 'int-leader', icon: IconTruckLoading },
        ],
      },
      {
        label: 'Services',
        value: 'int-atera',
        items: [
          { label: 'Atera', value: 'int-atera', icon: IconServer },
          { label: 'ESET', value: 'int-eset', icon: IconShieldLock },
          { label: 'Microsoft 365', value: 'int-m365', icon: IconBrandWindows },
          { label: 'Bitdefender', value: 'int-bitdefender', icon: IconShieldCheck },
        ],
      },
    ],
  },
  {
    label: 'Accounting',
    items: [
      { label: 'Xero', value: 'int-xero', icon: IconBuildingBank },
    ],
  },
  {
    label: 'Domains',
    items: [
      { label: 'Domains', value: 'int-domains-list', icon: IconWorld },
      { label: 'Domain Manager', value: 'int-domains', icon: IconWorldCog },
    ],
  },
];

// URL slug for each admin tab so every menu item is a resolvable route
// (e.g. /admin/service_catalog). Slugs are underscore-separated and strip the
// internal `int-` prefix; bare /admin falls back to the default tab. Keep this
// map in sync with the AdminTab union whenever a tab is added or renamed.
const TAB_TO_SLUG: Record<AdminTab, string> = {
  vendors: 'vendors',
  services: 'repair_services',
  'service-catalog': 'service_catalog',
  inventory: 'inventory',
  users: 'users',
  'app-settings': 'app_settings',
  'quote-terms': 'quote_terms',
  'check-in-terms': 'check_in_terms',
  bundles: 'bundles',
  'license-inventory': 'license_inventory',
  printers: 'printers',
  'autoprint-rules': 'autoprint_rules',
  'wall-display': 'wall_display',
  functions: 'functions',
  invoices: 'invoices',
  'recurring-tasks': 'recurring_tasks',
  processes: 'processes',
  'task-lists': 'task_lists',
  'task-dropdowns': 'task_dropdowns',
  'task-templates': 'task_templates',
  'ticket-templates': 'ticket_templates',
  todos: 'todos',
  'int-repairshopr': 'repairshopr',
  'int-pax8': 'pax8',
  'int-leader': 'leader',
  'int-atera': 'atera',
  'int-eset': 'eset',
  'int-m365': 'm365',
  'int-bitdefender': 'bitdefender',
  'int-xero': 'xero',
  'int-domains': 'domain_manager',
  'int-domains-list': 'domains',
  reconciliation: 'reconciliation',
  reporting: 'reporting',
  'product-pricing': 'product_pricing',
  'seat-ledger': 'seat_ledger',
  alerts: 'alerts',
  'sms-test': 'sms_test',
};

const SLUG_TO_TAB: Record<string, AdminTab> = Object.fromEntries(
  (Object.entries(TAB_TO_SLUG) as [AdminTab, string][]).map(([tab, slug]) => [slug, tab]),
);

const DEFAULT_ADMIN_TAB: AdminTab = 'vendors';

const tabFromSlug = (slug?: string): AdminTab =>
  (slug && SLUG_TO_TAB[slug]) || DEFAULT_ADMIN_TAB;

// Tabs whose pages have adopted the card-on-grey design system (DataTable kit
// + floating toolbar / cards). For these the content area is greyed and
// un-padded so the page controls its own layout; other admin pages keep the
// white p:3 area.
const CARD_SURFACE_TABS: AdminTab[] = [
  'vendors', 'functions', 'processes', 'invoices', 'recurring-tasks',
  'users', 'services', 'service-catalog', 'bundles', 'todos', 'app-settings',
  'task-lists', 'task-dropdowns', 'task-templates', 'ticket-templates',
  'int-repairshopr', 'printers', 'autoprint-rules', 'wall-display',
  'license-inventory',
  'reconciliation', 'product-pricing', 'seat-ledger', 'int-eset', 'int-m365',
  'quote-terms', 'check-in-terms',
  'sms-test',
];

interface AdminPageProps {
  // Slug from the /admin/:slug URL (undefined for bare /admin). Drives which
  // tab is shown so every menu item is deep-linkable.
  tabSlug?: string;
  // Push the new URL when a menu item is clicked. When omitted, the tab is
  // switched via local state only (no URL change).
  onSelectTab?: (slug: string) => void;
}

export const AdminPage: React.FC<AdminPageProps> = ({ tabSlug, onSelectTab }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>(() => tabFromSlug(tabSlug));

  // Keep the tab in sync with the URL slug (deep links, back/forward nav).
  useEffect(() => {
    setActiveTab(tabFromSlug(tabSlug));
  }, [tabSlug]);

  const handleSelectTab = (tab: AdminTab) => {
    setActiveTab(tab);
    onSelectTab?.(TAB_TO_SLUG[tab]);
  };

  const onCardSurfaceTab = CARD_SURFACE_TABS.includes(activeTab);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingVendor, setIsSavingVendor] = useState(false);
  const [mappingVendor, setMappingVendor] = useState<Vendor | null>(null);

  // Feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadVendors = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVendors(signal);
      setVendors(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadVendors(controller.signal);
    return () => controller.abort();
  }, [loadVendors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim() || !name.trim()) return;

    setSubmitting(true);
    try {
      await createVendor({ code: code.trim(), name: name.trim(), isActive });
      setCode('');
      setName('');
      setIsActive(true);
      setSnackbar({ open: true, message: 'Vendor created successfully', severity: 'success' });
      loadVendors();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create vendor';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = code.trim().length > 0 && name.trim().length > 0;

  const handleVendorSaved = async (
    vendorId: string,
    payload: UpdateVendorRequest,
  ): Promise<Vendor> => {
    // Persists the basic vendor fields. The dialog handles its own
    // closing, snackbar, and error display — this just hits the API,
    // patches the parent's vendors list, and surfaces the updated row
    // back to the dialog so it can refresh local state.
    setIsSavingVendor(true);
    setEditError(null);
    try {
      const updatedVendor = await updateVendor(vendorId, payload);
      setVendors((current) =>
        current.map((vendor) => (
          vendor.vendorId === updatedVendor.vendorId ? updatedVendor : vendor
        )),
      );
      return updatedVendor;
    } finally {
      setIsSavingVendor(false);
    }
  };

  if (mappingVendor) {
    return (
      <VendorMappingsPage
        vendor={mappingVendor}
        onBack={() => setMappingVendor(null)}
      />
    );
  }

  const renderPlaceholder = (label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <Typography variant="h6" color="text.secondary">{label} — Coming Soon</Typography>
    </Box>
  );

  const renderNavItem = (item: NavItem, nested = false) => {
    const Icon = item.icon;
    const isActive = activeTab === item.value;
    const className = [
      'admin-nav-item',
      nested ? 'admin-nav-item--nested' : '',
      isActive ? 'admin-nav-item--active' : '',
    ].filter(Boolean).join(' ');
    return (
      <button
        key={item.value}
        type="button"
        className={className}
        onClick={() => handleSelectTab(item.value)}
      >
        {Icon && <Icon className="admin-nav-item__icon" />}
        {item.label}
      </button>
    );
  };

  return (
    <div className="admin-page">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        {navGroups.map((group) => (
          <div className="admin-nav-group" key={group.label}>
            <div className="admin-nav-group__label">{group.label}</div>
            {group.items.map((item) =>
              item.items ? (
                <React.Fragment key={item.label}>
                  <div className="admin-nav-subgroup__label">{item.label}</div>
                  {item.items.map((sub) => renderNavItem(sub, true))}
                </React.Fragment>
              ) : (
                renderNavItem(item)
              )
            )}
          </div>
        ))}
      </aside>

      {/* Content Area */}
      <div className={`admin-content${onCardSurfaceTab ? '' : ' admin-content--surface'}`}>
        {activeTab === 'todos' && <ToDoTasksAdminPage />}
        {activeTab === 'recurring-tasks' && <RecurringTasksAdminPage />}
        {activeTab === 'task-lists' && <TaskListsAdminPage />}
        {activeTab === 'task-dropdowns' && <TaskDropdownsAdminPage />}
        {activeTab === 'task-templates' && <TemplateTaskConfigAdminPage />}
        {activeTab === 'ticket-templates' && <TicketTypeTemplatesAdminPage />}
        {activeTab === 'functions' && <SchedulerAdminPage />}
        {activeTab === 'processes' && <ProcessesAdminPage />}
        {activeTab === 'reconciliation' && <ReconciliationPage />}
        {activeTab === 'reporting' && <ReportingPage />}
        {activeTab === 'seat-ledger' && <SeatLedgerPage />}
        {activeTab === 'product-pricing' && <ProductPricingPage />}
        {activeTab === 'alerts' && <AlertsPage />}
        {activeTab === 'sms-test' && <SmsTestAdminPage />}

        {activeTab === 'services' && <RepairServicesAdminPage />}
        {activeTab === 'service-catalog' && <CustomerServicesCatalogAdminPage />}
        {activeTab === 'users' && <UsersAdminPage />}
        {activeTab === 'app-settings' && <AppSettingsAdminPage />}
        {activeTab === 'quote-terms' && <QuoteTermsTemplatesAdminPage />}
        {activeTab === 'check-in-terms' && <CheckInTermsTemplatesAdminPage />}
        {activeTab === 'bundles' && <BundlesAdminPage />}
        {activeTab === 'license-inventory' && <LicenseInventoryAdminPage />}
        {activeTab === 'printers' && <PrintersAdminPage />}
        {activeTab === 'autoprint-rules' && <AutoPrintRulesAdminPage />}
        {activeTab === 'wall-display' && <WallDisplayAdminPage />}
        {activeTab === 'invoices' && <InvoiceScheduleAdminPage />}
        {activeTab === 'int-repairshopr' && <RepairShoprSyncPage />}
        {activeTab === 'int-domains' && <DomainManagerPage />}
        {activeTab === 'int-domains-list' && <DomainsPage />}
        {activeTab === 'int-xero' && <XeroPage />}
        {activeTab === 'int-pax8' && renderPlaceholder('Pax8')}
        {activeTab === 'int-leader' && renderPlaceholder('Leader')}
        {activeTab === 'int-atera' && renderPlaceholder('Atera')}
        {activeTab === 'int-eset' && <EsetIntegrationAdminPage />}
        {activeTab === 'int-m365' && <M365TenantMapAdminPage />}
        {activeTab === 'int-bitdefender' && renderPlaceholder('Bitdefender')}

        {activeTab === 'vendors' && (
      <Box sx={{ p: '10px 16px 24px' }}>
      {/* Create Vendor Form */}
      <Box sx={[CARD_SX, { p: 2, mb: 1 }]}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            size="small"
            placeholder="e.g. MSFT"
            sx={{ width: 150 }}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            size="small"
            placeholder="e.g. Microsoft"
            sx={{ width: 250 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid || submitting}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </Box>
      </Box>

      {/* Vendors List */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Code</DataTableHeadCell>
                <DataTableHeadCell>Name</DataTableHeadCell>
                <DataTableHeadCell>Active</DataTableHeadCell>
                <DataTableHeadCell>Disti</DataTableHeadCell>
                <DataTableHeadCell>Retailer</DataTableHeadCell>
                <DataTableHeadCell>Subscription</DataTableHeadCell>
                <DataTableHeadCell align="right">EFT %</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <DataTableEmpty colSpan={8}>No vendors found</DataTableEmpty>
              ) : (
                vendors.map((vendor) => (
                  <DataTableRow key={vendor.vendorId}>
                    <DataTableCell bold>{vendor.code}</DataTableCell>
                    <DataTableCell>{vendor.name}</DataTableCell>
                    <DataTableCell>{vendor.isActive ? 'Yes' : 'No'}</DataTableCell>
                    <DataTableCell>{vendor.isDisti ? 'Yes' : 'No'}</DataTableCell>
                    <DataTableCell>{vendor.isRetailer ? 'Yes' : 'No'}</DataTableCell>
                    <DataTableCell>{vendor.isSubscriptionSupplier ? 'Yes' : 'No'}</DataTableCell>
                    <DataTableCell align="right">
                      {vendor.transactionFeeRate
                        ? `${(vendor.transactionFeeRate * 100).toFixed(2).replace(/\.?0+$/, '')}%`
                        : '—'}
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Mappings">
                        <IconButton size="small" onClick={() => setMappingVendor(vendor)}>
                          <AccountTreeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit vendor">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditError(null);
                            setEditingVendor(vendor);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <VendorEditDialog
        open={editingVendor !== null}
        vendor={editingVendor}
        isSaving={isSavingVendor}
        error={editError}
        onClose={() => {
          if (!isSavingVendor) {
            setEditingVendor(null);
            setEditError(null);
          }
        }}
        onSave={handleVendorSaved}
        onVendorUpdated={(updated) =>
          setVendors((current) =>
            current.map((vendor) =>
              vendor.vendorId === updated.vendorId ? updated : vendor,
            ),
          )
        }
      />
      </Box>
        )}
      </div>
    </div>
  );
};
